import '../../../../components/content/ContentTitle.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import StableDialog from '../../../../components/common/StableDialog';
import { DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { RatingStar } from './AlbumActionEditor';
import AlbumActionConfirm from './AlbumActionConfirm';
import { ratingAtPosition } from './albumActionModel';
import { changedTrackRatings, trackDrafts } from './trackRatingModel';
import './AlbumPrimaryActions.css';
import './Tracklist.css';

export function TrackRatingInput({ title, value, onChange, disabled, en, statusLabel = false }) {
  const dragging = useRef(false);
  const pick = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    onChange(ratingAtPosition(e.clientX - box.left, box.width));
  };
  return (
    <div className={`pp-track-rating-input${statusLabel ? ' pp-track-rating-input-labeled' : ''}`}>
      {statusLabel && (
        <output className={value == null ? 'is-unrated' : ''} aria-live="polite">
          {value == null
            ? en
              ? 'Choose a star rating'
              : '별점을 선택해 주세요'
            : `${en ? 'My rating' : '내 평점'} ${value.toFixed(1)}`}
        </output>
      )}
      <div
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${title} · ${en ? 'Track rating' : '곡 평점'}`}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={value ?? 0}
        aria-valuetext={value == null ? (en ? 'No Ratings' : '평가 없음') : value.toFixed(1)}
        aria-disabled={disabled}
        onPointerDown={(e) => {
          if (disabled) return;
          dragging.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          pick(e);
        }}
        onPointerMove={(e) => {
          if (dragging.current && !disabled) pick(e);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          const v = value ?? 0;
          const values = {
            ArrowLeft: Math.max(0, v - 0.5),
            ArrowDown: Math.max(0, v - 0.5),
            ArrowRight: Math.min(5, v + 0.5),
            ArrowUp: Math.min(5, v + 0.5),
            Home: 0,
            End: 5,
            Enter: v,
            ' ': v,
          };
          if (e.key in values) {
            e.preventDefault();
            onChange(values[e.key]);
          }
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i}>
            <RatingStar
              size={statusLabel ? 34 : 24}
              value={Math.max(0, Math.min(1, (value ?? 0) - i))}
            />
          </span>
        ))}
      </div>
      {!statusLabel && <output aria-live="polite">{value == null ? '' : value.toFixed(1)}</output>}
    </div>
  );
}
function RouteGuard({ dirty, busy, onBlocked }) {
  const blocker = useBlocker(dirty || busy);
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (busy) blocker.reset();
      else onBlocked(blocker);
    }
  }, [blocker, busy, onBlocked]);
  return null;
}
export default function TrackRatingEditor({ album, targetId, en, onClose, onSave }) {
  const [baseline, setBaseline] = useState(() => trackDrafts(album.tracks));
  const [drafts, setDrafts] = useState(() => trackDrafts(album.tracks));
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const target = useRef(null);
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const updates = changedTrackRatings(baseline, drafts);
  const dirty = updates.length > 0;
  useEffect(() => {
    mounted.current = true;
    const row = target.current;
    const body = row?.closest('.MuiDialogContent-root');
    if (body) body.scrollTop += row.getBoundingClientRect().top - body.getBoundingClientRect().top;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const protect = (e) => {
      if (dirty || busy) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [dirty, busy]);
  const close = () => {
    if (busyRef.current || confirm) return;
    if (dirty) setConfirm({});
    else onClose();
  };
  const save = async () => {
    if (busyRef.current || !dirty) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await onSave(updates);
      if (!mounted.current) return;
      if (result.failure) {
        // Successful members of a partial batch are now the retry baseline.
        setBaseline((previous) => ({
          ...previous,
          ...Object.fromEntries(result.successes.map((s) => [s.trackId, s.rating])),
        }));
        setError(result.failure.response?.status === 404 ? 'unavailable' : 'save');
      } else onClose();
    } catch {
      if (mounted.current) setError('save');
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <>
      {dataRouter && <RouteGuard dirty={dirty} busy={busy} onBlocked={setConfirm} />}
      <StableDialog
        open
        fullWidth
        maxWidth="sm"
        className="pp-album-dialog pp-track-dialog"
        onClose={close}
        aria-labelledby="track-editor-title"
        TransitionProps={{
          onEntered: () => {
            const row = target.current;
            const body = row?.closest('.MuiDialogContent-root');
            if (body)
              body.scrollTop += row.getBoundingClientRect().top - body.getBoundingClientRect().top;
            target.current?.querySelector('[role="slider"]')?.focus({ preventScroll: true });
          },
        }}
      >
        <DialogTitle id="track-editor-title" className="pp-album-dialog-title">
          {en ? 'Track Ratings' : '곡 평가'}
          <button type="button" onClick={close} disabled={busy} aria-label={en ? 'Close' : '닫기'}>
            <FiX size={20} />
          </button>
        </DialogTitle>
        <div className="pp-track-editor-summary">
          {album.imageUrl ? (
            <img
              src={album.imageUrl}
              alt=""
              onError={(e) => {
                e.currentTarget.style.visibility = 'hidden';
              }}
            />
          ) : (
            <span className="pp-track-editor-cover" />
          )}
          <div>
            <strong>
              {album.title}
              {album.contentRating === 'EXPLICIT' && (
                <span className="pp-track-explicit pp-inline-explicit">E</span>
              )}
            </strong>
            <p>{album.artist?.name}</p>
            <small>
              {en
                ? `${Object.values(drafts).filter((v) => v !== null).length} of ${album.ratingTrackCount ?? album.tracks.length} tracks rated · Edit all tracks at once`
                : `${Object.values(drafts).filter((v) => v !== null).length}/${album.ratingTrackCount ?? album.tracks.length}곡 평가함 · 모든 곡을 한 번에 수정할 수 있어요`}
            </small>
          </div>
        </div>
        <DialogContent className="pp-track-editor-list">
          {album.tracks
            .filter((t) => t.id != null)
            .map((track) => (
              <div
                key={track.id}
                ref={track.id === targetId ? target : null}
                className={`pp-track-editor-row ${track.id === targetId ? 'is-target' : ''}`}
              >
                <div className="pp-track-editor-heading">
                  <span>{String(track.trackNumber).padStart(2, '0')}</span>
                  <strong>
                    {track.title}
                    {track.contentRating === 'EXPLICIT' && (
                      <span className="pp-track-explicit pp-inline-explicit">E</span>
                    )}
                  </strong>
                  <small>
                    {track.ratingCount > 0
                      ? `${en ? 'Average' : '평균'} ${Number(track.averageRating ?? 0).toFixed(1)}`
                      : en
                        ? 'No Ratings'
                        : '평가 없음'}
                  </small>
                </div>
                <div className="pp-track-editor-control">
                  <TrackRatingInput
                    title={track.title}
                    value={drafts[track.id]}
                    disabled={busy}
                    en={en}
                    onChange={(value) => setDrafts((d) => ({ ...d, [track.id]: value }))}
                  />
                  <button
                    type="button"
                    className="pp-track-clear"
                    aria-label={`${track.title} · ${en ? 'Delete rating' : '곡 평가 삭제'}`}
                    disabled={busy || drafts[track.id] == null}
                    onClick={() => setDrafts((d) => ({ ...d, [track.id]: null }))}
                  >
                    {en ? 'Delete' : '삭제'}
                  </button>
                </div>
              </div>
            ))}
        </DialogContent>
        {error && (
          <p className="pp-track-error" role="alert">
            {error === 'unavailable'
              ? en
                ? 'Track ratings are unavailable on this server.'
                : '현재 서버에서 곡 평점 기능을 사용할 수 없습니다.'
              : en
                ? 'Could not save all ratings. Your changes are kept. Please try again.'
                : '곡 평점을 모두 저장하지 못했어요. 작성 내용은 유지됩니다. 다시 시도해 주세요.'}
          </p>
        )}
        <DialogActions className="pp-track-editor-footer">
          <button
            type="button"
            className="pp-album-submit"
            disabled={busy || !dirty}
            onClick={save}
          >
            {busy ? (en ? 'Saving…' : '저장 중…') : en ? 'Done' : '완료'}
          </button>
        </DialogActions>
      </StableDialog>
      {confirm && (
        <AlbumActionConfirm
          kind="discard"
          en={en}
          busy={busy}
          onCancel={() => {
            confirm.reset?.();
            setConfirm(null);
          }}
          onConfirm={() => {
            if (busyRef.current) return;
            confirm.proceed?.();
            setConfirm(null);
            onClose();
          }}
        />
      )}
    </>
  );
}
